// Matchmaker House interior — fullscreen DOM overlay with a pixelated
// Earth map. Players walk a tiny avatar across the map with WASD/arrows;
// standing near a region highlights it and E queues into that server.
//
// Per-region cards (ping + players + map name + phase) float above the
// country. Stacked sprite piles beneath each card show every player
// currently in the server: jumping if the match is playing/suddenDeath,
// sitting if it's warming up or ending. Server list comes from
// `fetchServerList()` (real PartyKit snapshot HTTP endpoint) with a
// local mock fallback so `?solo=1` still boots the screen instantly.

import { CONFIG } from '../config';
import { getActivePack } from '../art/packs';
import {
  fetchServerList,
  mockServerList,
  pingColor,
  type ServerEntry,
} from './server_query';

const MAP_W = 1400;
const MAP_H = 700;
const QUEUE_RADIUS_PX = 70;   // how close the avatar must be to queue
const AVATAR_SPEED = 320;     // px/sec
const AUTO_REFRESH_SEC = 3;   // matchmaker re-polls every N seconds

export function openMatchmakerHouse(
  onQueue: (entry: ServerEntry) => void,
  onClose?: () => void,
): () => void {
  // --- Overlay shell ---
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed; inset: 0;
    background: radial-gradient(circle at center, #0b1120 0%, #020617 75%);
    z-index: 9998;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    font: 700 14px system-ui, sans-serif;
    color: #e5e7eb; padding: 20px;
  `;

  const title = document.createElement('div');
  title.style.cssText = `
    font: 900 30px system-ui; letter-spacing: 5px;
    background: linear-gradient(90deg,#38bdf8,#22d3ee,#a78bfa);
    -webkit-background-clip: text; background-clip: text; color: transparent;
    margin-bottom: 6px;
    text-shadow: 0 0 40px rgba(14,165,233,.35);
  `;
  title.textContent = 'MATCHMAKER';
  overlay.appendChild(title);

  const subtitle = document.createElement('div');
  subtitle.style.cssText = `opacity:.65; font-size:11px; letter-spacing:2.5px; margin-bottom:18px;`;
  subtitle.textContent = 'WASD TO WALK · STAND ON A COUNTRY · E TO QUEUE';
  overlay.appendChild(subtitle);

  // --- Map frame ---
  const mapWrap = document.createElement('div');
  mapWrap.style.cssText = `
    position: relative;
    width: ${MAP_W}px; height: ${MAP_H}px;
    max-width: 96vw; max-height: 72vh;
    border: 2px solid #38bdf8; border-radius: 14px;
    box-shadow: 0 0 70px rgba(14,165,233,.35), inset 0 0 60px #020617;
    overflow: hidden; background: #071226;
  `;
  const canvas = document.createElement('canvas');
  canvas.width = MAP_W;
  canvas.height = MAP_H;
  canvas.style.cssText = `width:100%;height:100%;image-rendering:pixelated;display:block;`;
  drawPixelatedEarth(canvas);
  mapWrap.appendChild(canvas);

  // Server cards layer — absolute-positioned HTMLDivs driven by the UV
  // coordinate from the server list. Rebuilt on refresh.
  const cardsLayer = document.createElement('div');
  cardsLayer.style.cssText = `position:absolute;inset:0;pointer-events:none;`;
  mapWrap.appendChild(cardsLayer);

  // Walkable avatar — your own dot on the map. Starts near the middle
  // of the visible landmass so the player can see every region.
  const avatar = document.createElement('div');
  avatar.style.cssText = `
    position: absolute;
    width: 16px; height: 22px;
    transform: translate(-50%,-100%);
    pointer-events: none;
    z-index: 15;
    filter: drop-shadow(0 2px 0 rgba(0,0,0,.6));
  `;
  avatar.innerHTML = `
    <div style="
      width:12px;height:12px;margin:0 auto;
      background:#fde047;border:2px solid #020617;border-radius:3px;
      box-shadow:0 0 10px #fde04788;"></div>
    <div style="
      width:6px;height:8px;margin:2px auto 0;
      background:#ef4444;border:2px solid #020617;border-radius:2px;"></div>
  `;
  mapWrap.appendChild(avatar);

  const loadingBanner = document.createElement('div');
  loadingBanner.style.cssText = `
    position: absolute; top: 12px; right: 12px;
    background: rgba(2,6,23,.85); border: 1px solid #38bdf8;
    padding: 4px 10px; border-radius: 6px;
    font: 700 10px system-ui; letter-spacing: 1.5px; color: #7dd3fc;
  `;
  loadingBanner.textContent = 'SCANNING REGIONS…';
  mapWrap.appendChild(loadingBanner);

  overlay.appendChild(mapWrap);

  // --- Footer: legend + controls ---
  const footer = document.createElement('div');
  footer.style.cssText = `
    display: flex; align-items: center; gap: 32px; margin-top: 18px;
    flex-wrap: wrap; justify-content: center;
  `;
  footer.innerHTML = `
    <div style="display:flex;gap:16px;font-size:11px;opacity:.85;letter-spacing:1px;">
      <span style="display:flex;align-items:center;gap:6px;">
        <span style="width:10px;height:10px;background:#22c55e;border-radius:50%;box-shadow:0 0 8px #22c55e;"></span>&lt;80ms
      </span>
      <span style="display:flex;align-items:center;gap:6px;">
        <span style="width:10px;height:10px;background:#fde047;border-radius:50%;box-shadow:0 0 8px #fde047;"></span>80–160ms
      </span>
      <span style="display:flex;align-items:center;gap:6px;">
        <span style="width:10px;height:10px;background:#ef4444;border-radius:50%;box-shadow:0 0 8px #ef4444;"></span>&gt;160ms
      </span>
    </div>
    <button id="mm-refresh" style="
      padding: 8px 18px; border-radius: 8px; border: 1px solid #38bdf8;
      background: rgba(15,23,42,.8); color: #7dd3fc; cursor: pointer;
      font: 700 12px system-ui; letter-spacing: 1.5px; min-width: 160px;
    ">REFRESH [R] · <span id="mm-refresh-timer" style="color:#fde047;">3s</span></button>
    <button id="mm-cancel" style="
      padding: 8px 22px; border-radius: 8px; border: 1px solid #475569;
      background: rgba(15,23,42,.8); color: #e5e7eb; cursor: pointer;
      font: 700 12px system-ui; letter-spacing: 1.5px;
    ">CANCEL [ESC]</button>
  `;
  overlay.appendChild(footer);
  footer.querySelector<HTMLButtonElement>('#mm-cancel')!.addEventListener('click', () => close());
  // Invite link moved to the in-match pause menu — you can't usefully
  // share a room you haven't picked yet, and friends dropping into a
  // pre-match matchmaker don't benefit from a link back to "before".
  const refreshBtn = footer.querySelector<HTMLButtonElement>('#mm-refresh')!;
  const refreshTimerEl = footer.querySelector<HTMLSpanElement>('#mm-refresh-timer')!;
  // Any manual refresh path (button click, R key) routes through here
  // so the countdown resets uniformly instead of ticking past zero
  // immediately after a user-triggered poll.
  function manualRefresh() {
    reload(true);
    autoRefreshSecs = AUTO_REFRESH_SEC;
    refreshTimerEl.textContent = `${AUTO_REFRESH_SEC}s`;
  }
  refreshBtn.addEventListener('click', manualRefresh);

  document.body.appendChild(overlay);

  // --- Runtime state ---
  let servers: ServerEntry[] = mockServerList();
  // Avatar pixel position (in map-local coords)
  const avatarPos = { x: MAP_W * 0.32, y: MAP_H * 0.45 };
  const keys = new Set<string>();
  let focusedRoom: string | null = null;
  let closed = false;
  let rafId = 0;
  let lastT = performance.now();
  const startMs = performance.now();
  // Auto-refresh countdown — re-polls the snapshot endpoint every
  // AUTO_REFRESH_SEC seconds so idle DOs cycling their random mode/arena
  // are reflected without manual clicks. The visible timer counts down
  // in the REFRESH button label and resets on any reload (manual or auto).
  let autoRefreshSecs = AUTO_REFRESH_SEC;

  function keyDown(e: KeyboardEvent) {
    if (closed) return;
    if (e.code === 'Escape') { close(); return; }
    if (e.code === 'KeyR')   { manualRefresh(); return; }
    if (e.code === 'KeyE' || e.code === 'Enter') {
      const target = servers.find((s) => s.room === focusedRoom);
      if (target) {
        onQueue(target);
        close();
      }
      return;
    }
    keys.add(e.code);
  }
  function keyUp(e: KeyboardEvent) { keys.delete(e.code); }
  addEventListener('keydown', keyDown);
  addEventListener('keyup', keyUp);

  // Render initial mock list so the UI is never blank while the HTTP
  // fetch is in flight.
  renderCards();

  // Kick off real snapshot fetch.
  reload(false);

  // Animation loop — avatar movement, sprite bob, focus detection.
  function frame(now: number) {
    if (closed) return;
    const dt = Math.min(0.05, (now - lastT) / 1000);
    lastT = now;

    // Movement
    let vx = 0;
    let vy = 0;
    if (keys.has('KeyA') || keys.has('ArrowLeft'))  vx -= 1;
    if (keys.has('KeyD') || keys.has('ArrowRight')) vx += 1;
    if (keys.has('KeyW') || keys.has('ArrowUp'))    vy -= 1;
    if (keys.has('KeyS') || keys.has('ArrowDown'))  vy += 1;
    if (vx !== 0 || vy !== 0) {
      const len = Math.hypot(vx, vy);
      avatarPos.x += (vx / len) * AVATAR_SPEED * dt;
      avatarPos.y += (vy / len) * AVATAR_SPEED * dt;
      avatarPos.x = clamp(avatarPos.x, 8, MAP_W - 8);
      avatarPos.y = clamp(avatarPos.y, 16, MAP_H - 4);
    }
    avatar.style.left = `${(avatarPos.x / MAP_W) * 100}%`;
    avatar.style.top  = `${(avatarPos.y / MAP_H) * 100}%`;

    // Tiny bob for the avatar so it looks alive
    const bob = Math.sin(now / 220) * 1.2;
    avatar.style.transform = `translate(-50%, calc(-100% + ${bob.toFixed(2)}px))`;

    // Focus detection — nearest server within QUEUE_RADIUS_PX
    let bestRoom: string | null = null;
    let bestDist = QUEUE_RADIUS_PX;
    for (const s of servers) {
      const px = s.u * MAP_W;
      const py = s.v * MAP_H;
      const d = Math.hypot(px - avatarPos.x, py - avatarPos.y);
      if (d < bestDist) { bestDist = d; bestRoom = s.room; }
    }
    if (bestRoom !== focusedRoom) {
      focusedRoom = bestRoom;
      applyFocusStyling();
    }

    // Bob/sit animation on per-server sprite piles
    animateSpritePiles(now);

    // Live phase-timer tick-down between the 5 s polls. Walk the
    // rendered cards and rewrite their phase span based on the entry's
    // last-fetched remainingSec minus wall-clock elapsed. Cheap: ≤6
    // cards, no DOM rebuilds, just textContent + color.
    for (const node of cardsLayer.querySelectorAll<HTMLDivElement>('[data-room]')) {
      const entry = servers.find((s) => s.room === node.dataset.room);
      if (!entry) continue;
      const span = node.querySelector<HTMLSpanElement>('.mm-phase');
      if (!span) continue;
      const elapsed = (Date.now() - entry.fetchedAtMs) / 1000;
      const live = Math.max(0, entry.remainingSec - elapsed);
      span.textContent = phaseLabelFor(entry, live);
      span.style.color = phaseColor(entry);
    }

    // Auto-refresh countdown — decrement wall-clock seconds; at 0 kick
    // off another reload + reset. Non-blocking (fetch runs async).
    autoRefreshSecs -= dt;
    if (autoRefreshSecs <= 0) {
      autoRefreshSecs = AUTO_REFRESH_SEC;
      reload(true);
    }
    refreshTimerEl.textContent = `${Math.max(1, Math.ceil(autoRefreshSecs))}s`;

    rafId = requestAnimationFrame(frame);
  }
  rafId = requestAnimationFrame(frame);

  async function reload(force: boolean) {
    loadingBanner.style.display = 'block';
    loadingBanner.textContent = 'SCANNING REGIONS…';
    try {
      const list = await fetchServerList(CONFIG.PARTY_HOST, { force });
      if (closed) return;
      servers = list;
      renderCards();
      const mocked = servers.filter((s) => s.mock).length;
      loadingBanner.textContent = mocked === 0 ? 'LIVE' : mocked === servers.length ? 'OFFLINE (MOCK)' : `${servers.length - mocked}/${servers.length} LIVE`;
      loadingBanner.style.color = mocked === servers.length ? '#fb923c' : '#7dd3fc';
    } catch {
      loadingBanner.textContent = 'OFFLINE (MOCK)';
      loadingBanner.style.color = '#fb923c';
    }
  }

  function renderCards() {
    cardsLayer.innerHTML = '';
    for (const s of servers) {
      const holder = document.createElement('div');
      holder.dataset.room = s.room;
      holder.style.cssText = `
        position: absolute;
        left: ${s.u * 100}%; top: ${s.v * 100}%;
        transform: translate(-50%, -100%);
        pointer-events: none;
        transition: transform .14s ease-out, filter .14s ease-out;
      `;
      holder.innerHTML = regionCardHtml(s) + spritePileHtml(s);
      cardsLayer.appendChild(holder);
    }
    applyFocusStyling();
  }

  function applyFocusStyling() {
    for (const node of cardsLayer.querySelectorAll<HTMLDivElement>('[data-room]')) {
      const isFocused = node.dataset.room === focusedRoom;
      node.style.transform = isFocused
        ? 'translate(-50%, -106%) scale(1.12)'
        : 'translate(-50%, -100%) scale(1)';
      node.style.zIndex = isFocused ? '20' : '';
      // Avoid `filter: drop-shadow` on the holder — it spawns a new
      // stacking context that breaks the inner card's `backdrop-filter`
      // on Chromium, making the focused card look empty. Glow is
      // applied via box-shadow on the inner body instead.
      const body = node.querySelector<HTMLDivElement>('.mm-card-body');
      if (body) {
        const color = body.dataset.color ?? '#fde047';
        body.style.background = isFocused ? 'rgba(2,6,23,.92)' : 'rgba(2,6,23,.55)';
        body.style.borderColor = isFocused ? '#fde047' : color;
        body.style.boxShadow = isFocused
          ? `0 12px 34px rgba(0,0,0,.85), 0 0 0 2px #fde047, 0 0 36px #fde04788`
          : `0 10px 28px rgba(0,0,0,.7), 0 0 24px ${color}55`;
      }
      const badge = node.querySelector<HTMLDivElement>('.mm-queue-hint');
      if (badge) badge.style.opacity = isFocused ? '1' : '0';
    }
  }

  function animateSpritePiles(now: number) {
    const tMs = now - startMs;
    for (const node of cardsLayer.querySelectorAll<HTMLDivElement>('.mm-sprite')) {
      const phase = node.dataset.phase ?? 'warmup';
      const i = Number(node.dataset.index ?? '0');
      const playing = phase === 'playing' || phase === 'suddenDeath';
      if (playing) {
        const bob = Math.sin((tMs / 180) + i * 0.8) * 4 - 1.5;
        node.style.transform = `translateY(${bob.toFixed(2)}px)`;
      } else {
        node.style.transform = `translateY(3px) scaleY(.75)`;
      }
    }
  }

  function close() {
    if (closed) return;
    closed = true;
    cancelAnimationFrame(rafId);
    removeEventListener('keydown', keyDown);
    removeEventListener('keyup', keyUp);
    overlay.remove();
    onClose?.();
  }

  return close;
}

function regionCardHtml(s: ServerEntry): string {
  const color = pingColor(s.pingMs);
  const phaseLabel = phaseLabelFor(s, s.remainingSec);
  const mockTag = s.mock ? `<span style="color:#fb923c;opacity:.85;">MOCK</span>` : '';
  return `
    <div class="mm-card-body" data-color="${color}" style="
      padding: 10px 14px;
      background: rgba(2,6,23,.55);
      backdrop-filter: blur(3px);
      -webkit-backdrop-filter: blur(3px);
      border: 1.5px solid ${color};
      border-radius: 10px;
      box-shadow: 0 10px 28px rgba(0,0,0,.7), 0 0 24px ${color}55;
      min-width: 150px;
      text-align: center;
      pointer-events: none;
      transition: background .14s ease-out, box-shadow .14s ease-out, border-width .14s ease-out;
    ">
      <div style="font:900 12px system-ui;letter-spacing:1.5px;color:${color};">${s.label}</div>
      <div style="display:flex;justify-content:center;gap:6px;margin-top:2px;font:700 10px system-ui;opacity:.85;">
        <span style="color:#a78bfa;">${s.modeLabel}</span>
        <span style="opacity:.45;">·</span>
        <span>${s.mapName}</span>
      </div>
      <div style="display:flex;justify-content:center;gap:12px;margin-top:6px;font:800 12px system-ui;">
        <span style="color:#60a5fa;">${s.virgin}</span>
        <span style="opacity:.4;">/</span>
        <span style="color:#fb923c;">${s.chad}</span>
      </div>
      <div style="display:flex;justify-content:center;gap:6px;margin-top:3px;font:700 9px system-ui;opacity:.75;letter-spacing:.5px;">
        <span>${s.pingMs}ms</span>
        <span>·</span>
        <span class="mm-phase" style="color:${phaseColor(s)};">${phaseLabel}</span>
        ${mockTag ? `<span>·</span>${mockTag}` : ''}
      </div>
      <div class="mm-queue-hint" style="
        margin-top:6px; font:900 10px system-ui; letter-spacing:2px;
        color:#fde047; opacity:0; transition:opacity .2s;
      ">[ E ] QUEUE</div>
    </div>
    <div style="
      position: absolute; left: 50%; top: 100%;
      transform: translate(-50%, -60%) rotate(45deg);
      width: 12px; height: 12px;
      background: ${color}; border-radius: 2px;
      box-shadow: 0 0 14px ${color};
    "></div>
  `;
}

// Stacked player markers beneath each card — one square per human
// player in the room, alternating Virgin blue / Chad orange.
// Offsets are computed via CSS only; the frame loop updates the
// per-element transform for the bob/sit animation.
function spritePileHtml(s: ServerEntry): string {
  const total = Math.min(10, s.virgin + s.chad);
  if (total === 0) return '';
  const blocks: string[] = [];
  let remainingO = s.virgin;
  let remainingI = s.chad;
  for (let i = 0; i < total; i++) {
    // Alternate teams when both present, otherwise color by whichever team has stock
    let isVirgin: boolean;
    if (remainingO > 0 && remainingI > 0) {
      isVirgin = i % 2 === 0;
    } else {
      isVirgin = remainingO > 0;
    }
    if (isVirgin) remainingO--; else remainingI--;
    const color = isVirgin ? '#60a5fa' : '#fb923c';
    blocks.push(`
      <div class="mm-sprite"
           data-phase="${s.phase}"
           data-index="${i}"
           style="
             position:absolute;
             left: 50%;
             bottom: ${6 + i * 7}px;
             width: 8px; height: 10px;
             margin-left: -4px;
             background: ${color};
             border: 1.5px solid #020617;
             border-radius: 2px;
             box-shadow: 0 2px 0 rgba(0,0,0,.55);
             will-change: transform;
           "></div>
    `);
  }
  return `
    <div style="
      position: absolute; left: 50%; top: 100%;
      transform: translateX(-50%);
      width: 60px; height: ${Math.max(16, 18 + total * 7)}px;
      pointer-events: none;
    ">${blocks.join('')}</div>
  `;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// Format seconds → "m:ss" for live match timer.
function formatClock(sec: number): string {
  const total = Math.max(0, Math.floor(sec));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Card phase label — STANDBY for empty rooms, otherwise phase + countdown.
function phaseLabelFor(entry: ServerEntry, sec: number): string {
  if (entry.standby) return 'STANDBY';
  const clock = formatClock(sec);
  switch (entry.phase) {
    case 'playing':     return `LIVE · ${clock}`;
    case 'suddenDeath': return `SD · ${clock}`;
    case 'warmup':      return `WARMUP · ${clock}`;
    default:            return `ENDING · ${clock}`;
  }
}

function phaseColor(entry: ServerEntry): string {
  if (entry.standby) return '#94a3b8';
  if (entry.phase === 'playing' || entry.phase === 'suddenDeath') return '#86efac';
  return '#e5e7eb';
}

// Delegated to the active art pack — lets the player swap the world-map
// style from the settings modal. Implementations live in src/art/packs/.
function drawPixelatedEarth(canvas: HTMLCanvasElement) {
  getActivePack().drawPixelatedEarth(canvas);
}
